import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { toast } from 'react-toastify';

import ScriptContext from '../../../context/Script';
import versionService from '../../../services/versionService.js';
import analyticsService from '../../../services/analyticsService.js';
import DuplicateVersionModal from '../../Modals/project/duplicateVersionModal.js';
import UpgradeMembership from '../../Modals/Subscription/upgradeMembership.js';

const COLOR_SWATCHES = {
  White: '#FFFFFF',
};

// Select values for the action rows at the bottom of the menu -- they aren't
// versions, so onChange intercepts them instead of switching.
const DUPLICATE = '__duplicate';
const UPLOAD = '__upload';

function versionNumber(v) {
  return v.version_minor > 0 ? `v${v.version_major}.${v.version_minor}` : `v${v.version_major}`;
}

// The revision name shown next to the number, e.g. "White draft".
function revisionName(v) {
  return `${v.version_color} ${v.version_label || 'draft'}`;
}

function versionDisplay(v) {
  const label = v.version_label ? ` — ${v.version_label}` : '';
  return `${versionNumber(v)} · ${v.version_color}${label}`;
}

function ColorDot({ color }) {
  return (
    <Box
      component="span"
      sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        bgcolor: COLOR_SWATCHES[color] || '#CCCCCC',
        border: '1px solid rgba(0,0,0,0.35)',
        flexShrink: 0,
      }}
    />
  );
}

const ScriptVersionSelect = () => {
  const { script } = useContext(ScriptContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [showMembershipModal, setShowMembershipModal] = useState(false);

  if (!script || !script.id) return null;

  const rootScriptId = script.root_script_id || script.id;
  const current = {
    version_major: script.version_major || 1,
    version_minor: script.version_minor || 0,
    version_color: script.version_color || 'White',
    version_label: script.version_label || null,
  };

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const data = await versionService.getVersions(rootScriptId);
      setVersions(data.versions || []);
    } catch (err) {
      console.error('Error loading versions', err);
      setVersions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectVersion = async (version) => {
    if (version.id === script.id) return;

    // Persist the choice here (rather than relying on the URL alone) so
    // switching back to the major version after being on a duplicate always
    // lands correctly, regardless of what was previously persisted.
    let activated;
    try {
      activated = await versionService.activate(rootScriptId, version.version_minor);
    } catch (err) {
      console.error('Error switching version', err);
      toast.error('Unable to switch versions. Please try again.');
      return;
    }

    if (!activated || activated.id !== version.id) {
      console.error('Version activation mismatch', { requested: version, activated });
      toast.error('Unable to switch versions. Please try again.');
      return;
    }

    // Hand the already-resolved version to the route effect that's about to
    // fire from navigate() below, so it doesn't re-resolve (and potentially
    // land somewhere else -- see versionService.consumeJustActivated).
    versionService.rememberActivated(rootScriptId, activated);

    toast.success(`Switched to ${versionDisplay(activated)}`, { autoClose: 2000, hideProgressBar: true });
    const path =
      activated.version_minor > 0
        ? `/app/shotList/${rootScriptId}/v/${activated.version_minor}`
        : `/app/shotList/${rootScriptId}`;
    navigate(path);
  };

  const handleChange = (event) => {
    const value = event.target.value;
    if (value === UPLOAD) {
      analyticsService.trackEvent('versioning_upload_interest', { root_script_id: rootScriptId });
      return;
    }
    if (value === DUPLICATE) {
      setDuplicateOpen(true);
      return;
    }
    const version = (versions || []).find((v) => v.id === value);
    if (version) handleSelectVersion(version);
  };

  const nextMinor = versions && versions.length ? Math.max(0, ...versions.map((v) => v.version_minor)) + 1 : 1;
  // "v1" for a major version, "v1.2" for a minor -- used so "Duplicate
  // current" reads as "copy of v1 White" / "copy of v1.2 White" instead of
  // just "copy of White", which was ambiguous about which White version.
  const currentNumber = versionNumber(current);

  const majorVersions = (versions || []).filter((v) => v.version_minor === 0);
  const minorVersions = (versions || []).filter((v) => v.version_minor > 0);
  // Ordered so each minor version follows its major one.
  const orderedVersions = majorVersions.flatMap((major) => [
    major,
    ...minorVersions.filter((m) => m.version_major === major.version_major),
  ]);
  const currentListed = orderedVersions.some((v) => v.id === script.id);

  const handleDuplicated = (version) => {
    setDuplicateOpen(false);
    versionService.rememberActivated(rootScriptId, version);
    toast.success(`Created ${versionDisplay(version)}`, { autoClose: 2000, hideProgressBar: true });
    const path =
      version.version_minor > 0
        ? `/app/shotList/${rootScriptId}/v/${version.version_minor}`
        : `/app/shotList/${rootScriptId}`;
    navigate(path);
  };

  return (
    <>
      <FormControl sx={{ width: { xs: 176, md: 216 }, flexShrink: 0 }}>
        <InputLabel id="script-version-label" shrink>
          Script version
        </InputLabel>
        <Select
          labelId="script-version-label"
          label="Script version"
          notched
          value={script.id}
          open={open}
          onOpen={handleOpen}
          onClose={() => setOpen(false)}
          onChange={handleChange}
          renderValue={() => (
            <>
              <ColorDot color={current.version_color} />
              <Typography variant="body2" component="span" fontWeight={500}>
                {currentNumber}
              </Typography>
              <Typography variant="body2" component="span" color="text.secondary" noWrap>
                {revisionName(current)}
              </Typography>
            </>
          )}
          MenuProps={{ PaperProps: { sx: { width: 360 } } }}
        >
          <ListSubheader>PROJECT VERSIONS</ListSubheader>

          {loading && (
            <MenuItem disabled>
              <Typography variant="body2">Loading…</Typography>
            </MenuItem>
          )}

          {/* Keeps the current value valid before the version list loads. */}
          {(loading || !currentListed) && (
            <MenuItem value={script.id} sx={{ display: 'none' }} />
          )}

          {!loading &&
            orderedVersions.map((version) => {
              const isMinor = version.version_minor > 0;
              const isCurrent = version.id === script.id;
              return (
                <MenuItem key={version.id} value={version.id} sx={{ pl: isMinor ? 4 : 2 }}>
                  <ListItemText
                    primary={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!isMinor && <ColorDot color={version.version_color} />}
                        <span>{versionDisplay(version)}</span>
                        {isCurrent && <CheckIcon fontSize="small" color="primary" />}
                        {isCurrent && <Chip label="Current" color="primary" variant="outlined" />}
                      </Box>
                    }
                    secondary={
                      isMinor
                        ? `duplicated ${new Date(version.created_at).toLocaleDateString()} · ${version.shot_count} shots`
                        : `${version.script_name} · ${new Date(version.created_at).toLocaleDateString()} · ${version.shot_count} shots`
                    }
                  />
                </MenuItem>
              );
            })}

          <Divider />

          <MenuItem value={UPLOAD}>
            <ListItemText primary="Upload new script" />
            <Chip label="Coming soon" color="primary" variant="outlined" />
          </MenuItem>

          <MenuItem value={DUPLICATE}>
            <ListItemText
              primary="Duplicate current"
              secondary={`→ v${current.version_major}.${nextMinor} · copy of ${currentNumber} ${current.version_color}`}
            />
          </MenuItem>
        </Select>
      </FormControl>

      <DuplicateVersionModal
        open={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        rootScriptId={rootScriptId}
        currentVersion={current}
        nextMinor={nextMinor}
        onDuplicated={handleDuplicated}
        onProRequired={() => {
          analyticsService.trackEvent('upgrade_cta_clicked', { source: 'version_duplicate' });
          setShowMembershipModal(true);
        }}
      />

      <UpgradeMembership
        isOpen={showMembershipModal}
        onRequestClose={() => setShowMembershipModal(false)}
        featureMsg="Versioning is a Pro feature — upgrade to duplicate this draft."
        scriptId={script?.id}
      />
    </>
  );
};

export default ScriptVersionSelect;
